<template>
  <div class="new-project-view-container">
    <div class="new-project-header-container">
      <div class="breadcrumb-container">New Project&nbsp;|&nbsp; <strong> Project Information</strong></div>
      <div class="new-project-main-title">New Project</div>
      <div>Tell us about your project. You will be able to upload your first document on the next step!</div>
    </div>



    <!-- Project Information -->
    <div>
      <div class="divider">
        <div class="accordion-panel-header">
          <label for="projectInformation"><strong>Project Information</strong></label>
          <ExpandCollapse name="projectInformation" :is-open-initially="isProjectInformationOpen"
            :is-vertical-center="false" open-state="down" closed-state="up" :show-double-chevron="false"
            :show-circle="false" :chevron-fill="'#4A5FD5'"
            @toggle-expand-collapse="expandCollapseController('projectInformation');" />
        </div>
      </div>
      <div v-if="isProjectInformationOpen">
        <ProjectInformation ref="projectInformationRef" @image-selected="onProjectImageSelected"></ProjectInformation>
      </div>
    </div>

    <!-- Team Information -->
    <div>
      <div class="divider">
        <div class="accordion-panel-header">
          <label for="teamInformation"><strong>Team Information</strong></label>
          <ExpandCollapse name="teamInformation" :is-open-initially="isTeamInformationOpen" :is-vertical-center="false"
            open-state="down" closed-state="up" :show-double-chevron="false" :show-circle="false"
            :chevron-fill="'#4A5FD5'" @toggle-expand-collapse="expandCollapseController('teamInformation');" />
        </div>
      </div>
      <div v-if="isTeamInformationOpen">
        <ProjectTeamInformation ref="projectTeamInformationRef"></ProjectTeamInformation>
      </div>
    </div>
    
    <!-- Schedule Information - REMOVED: Caused 404 errors, not essential for project creation -->
    <!-- Projects can have schedule information added later through other means -->

    <!-- Upload Documents -->
    <!-- TODO - as of 12/20/2024, this area has been confusing process-wise, so just disable it and assume the person has to go to the upload projects page -->
<!--    <div>-->
<!--      <div class="divider">-->
<!--        <div class="accordion-panel-header">-->
<!--          <label for="uploadDocuments"><strong>Upload Documents</strong></label>-->
<!--          <ExpandCollapse name="uploadDocuments" :is-open-initially="isUploadDocumentsOpen" :is-vertical-center="false"-->
<!--            open-state="down" closed-state="up" :show-double-chevron="false" :show-circle="false"-->
<!--            :chevron-fill="'#4A5FD5'" @toggle-expand-collapse="expandCollapseController('uploadDocuments');" />-->
<!--        </div>-->
<!--      </div>-->
<!--      <div v-if="isUploadDocumentsOpen">-->
<!--        <ProjectDocuments ref="projectDocuments"></ProjectDocuments>-->
<!--      </div>-->
<!--    </div>-->

    <div class="project-action-button">
      <button @click="createNewProject">Complete Project</button>
    </div>

    <error-message class="error-message" />
    <success-message class="success-message" />
  </div>
</template>

<script setup>
import axios from 'axios';
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import ExpandCollapse from "@/components/helpers/ExpandCollapse.vue";
import ProjectInformation from "@/components/add-project/ProjectInformation.vue";
import ProjectTeamInformation from "@/components/add-project/ProjectTeamInformation.vue";
// import ProjectScheduleInformation from "@/components/add-project/ProjectScheduleInformation.vue"; // REMOVED: Caused 404 errors
import ProjectImportButton from "@/components/ProjectImportButton.vue";
import { useErrorStore } from "@/stores/error_store";
import { useSuccessStore } from "@/stores/success_store";
import { useDropdownStore } from "@/stores/dropdown_store";
import { useAuthStore } from "@/stores/auth_store";
import { addGpsCoordinates } from "@/services/geocodingService";
import ErrorMessage from "@/components/helpers/ErrorMessage.vue"
import SuccessMessage from "@/components/helpers/SuccessMessage.vue"

// API settings
const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://localhost:3001/api/v1/"
const authStore = useAuthStore();
const router = useRouter();
const errorStore = useErrorStore();
const successStore = useSuccessStore();
const dropdownStore = useDropdownStore();
const isProjectInformationOpen = ref(true);
const isTeamInformationOpen = ref(false);
const isUploadDocumentsOpen = ref(false);
const selectedProjectImage = ref(null);

function onProjectImageSelected(file) {
  selectedProjectImage.value = file;
  console.log('📸 Parent received image:', file?.name || 'null');
}

const expandCollapseController = (element) => {
  switch (element) {
    case 'projectInformation':
      isProjectInformationOpen.value = !isProjectInformationOpen.value
      break;
    case 'teamInformation':
      isTeamInformationOpen.value = !isTeamInformationOpen.value
      break;
    // case 'scheduleInformation': // REMOVED: Schedule section removed
    //   isScheduleInformationOpen.value = !isScheduleInformationOpen.value
    //   break;
    case 'uploadDocuments':
      isUploadDocumentsOpen.value = !isUploadDocumentsOpen.value
      break;
    default:
      break;
  }
}

const handleProjectImportComplete = (projectData) => {
  // Show success message
  successStore.setSuccess('Bulk Project Import', `Project "${projectData.name}" imported successfully! Processing ${projectData.total_documents} documents in background.`, 'success', false).then(() => {
    // After showing success, redirect to project documents or refresh
    if (successStore.clearedStatus === 'confirm') {
      // Could redirect to project page or stay here
      window.location.reload();
    }
  });
  
  // Optionally, you could navigate to the project documents page:
  // this.$router.push(`/projects/${projectData.id}/documents`);
}

const projectInformationRef = ref(null);
const projectTeamInformationRef = ref(null);
// const projectScheduleInformationRef = ref(null); // REMOVED: Schedule section removed
// const projectDocuments = ref(null); // TODO: Comment this as Upload Documents is not used as of this moment - PLEASE DO NOT DELETE

async function createNewProject() {
  // Check that required Project Information form is loaded and open
  if (!projectInformationRef.value || !projectInformationRef.value.form) {
    errorStore.setError('Form Error', 'Project information form is required. Please expand the Project Information section and fill out the required fields.', 'error', false);
    return;
  }
  
  // Team Information is optional if the section is closed
  let projectTeamInformationFormData = null;
  if (isTeamInformationOpen.value) {
    if (!projectTeamInformationRef.value || !projectTeamInformationRef.value.form) {
      errorStore.setError('Form Error', 'Project team information form is not properly loaded. Please refresh the page and try again, or close the Team Information section if it\'s not needed.', 'error', false);
      return;
    }
    projectTeamInformationFormData = projectTeamInformationRef.value.form;
  } else {
    // Provide default values when team section is closed
    projectTeamInformationFormData = {
      primaryGeneralContractor: { name: '', is_primary: false },
      affiliatedWithDeveloper: false,
      investors: [],
      consultants: []
    };
  }
  
  const projectInformationFormData = projectInformationRef.value.form;
  // const projectScheduleInformationFormData = projectScheduleInformationRef.value?.form || { scopeSchedules: [], budgetDocument: null }; // REMOVED
  // const projectBaselineDocumentsFormData = projectDocuments.value.baselineDocuments; // TODO: Comment this as Upload Documents is not used as of this moment - PLEASE DO NOT DELETE
  // const projectDrawDocumentsFormData = projectDocuments.value.drawDocuments; // TODO: Comment this as Upload Documents is not used as of this moment - PLEASE DO NOT DELETE

  // Project Information
  const projectValidData = {
    project: {
      general_info: {
        image_url: null,
        name: projectInformationFormData.buildingName,
        organization_id: 1, // Default organization - required for project creation
        status: projectInformationFormData.constructionStatus,
        entity_name: projectInformationFormData.buildingName, // Keep it as it is for now
        building_name: projectInformationFormData.buildingName, // Keep it as it is for now
        address_line_1: projectInformationFormData.streetAddress,
        address_line_2: null, // keep it null for now
        city: projectInformationFormData.city,
        state: projectInformationFormData.state,
        zipcode: projectInformationFormData.zip,
        finish_level: null, // keep it null for now
        notes: projectInformationFormData.notes,
        total_gsf: Number(projectInformationFormData.totalSquareFeetPerUse),
        total_num_units: Number(projectInformationFormData.totalNumberOfUnits),
        subtotal_building_gsf: projectInformationFormData.subTotalBuildingGSF,
        subtotal_num_units: projectInformationFormData.subTotalBuildingNumUnits,
        subtotal_parking_gsf: projectInformationFormData.subTotalParkingGSF,
        subtotal_num_spaces: projectInformationFormData.subTotalParkingNumSpaces,
        planned_start_date: projectInformationFormData.plannedStartDate,
        planned_end_date: projectInformationFormData.plannedEndDate,
        actual_start_date: projectInformationFormData.actualStartDate,
        actual_end_date: projectInformationFormData.actualEndDate,
      },
      construction_info: {
        construction_types: buildNewConstructionTypes(projectInformationFormData.constructionTypes, projectInformationFormData.constructionTypeAdaptiveReuse),
        construction_contract_type_id: projectInformationFormData.constructionContractType,
        num_floors: projectInformationFormData.buildingHeightStories,
        total_height: projectInformationFormData.buildingHeightFeet,
        is_historic_has_landmark: projectInformationFormData.historicLandmarksComponent === 'true',
        sustainability_type_id: projectInformationFormData.sustainabilityType,
        is_modular: projectInformationFormData.modularBuild === 'true',
      },
      building_use_info: {
        new_uses: buildNewUsesWithSubCategories(projectInformationFormData),
        parking_types: buildNewParkingTypes(projectInformationFormData)
      },
      amenities_info: {
        amenities: projectInformationFormData.amenities
      },
      team_info: {
        investor_sponsor_info: {
          investors: buildInvestors(projectTeamInformationFormData)
        },
        consultant_info: {
          consultants: buildConsultants(projectTeamInformationFormData)
       },
       contractor_info: {
        contractors: (projectTeamInformationFormData.primaryGeneralContractor?.name || '').trim() !== '' ? [
          {
            name: projectTeamInformationFormData.primaryGeneralContractor.name,
            is_primary: projectTeamInformationFormData.primaryGeneralContractor.is_primary || false,
            is_affiliated_with_developer: projectTeamInformationFormData.affiliatedWithDeveloper || false
          }
        ] : []
        }
      },
      // schedule_info: { // REMOVED: No longer using schedule information
      //   scope_schedules: []
      // }
    }
  }

  // 🌍 AUTOMATIC GEOCODING: Convert address to GPS coordinates
  console.log('🌍 Starting automatic geocoding for project address...');
  try {
    await addGpsCoordinates(projectValidData.project.general_info);
    console.log('✅ GPS coordinates added successfully');
  } catch (error) {
    console.warn('⚠️ Geocoding failed, proceeding without coordinates:', error.message);
    // Continue with project creation even if geocoding fails
  }

  // Use fetch API directly since authentication is bypassed on backend
  console.log('🚀 Sending project data to backend:', JSON.stringify(projectValidData, null, 2));
  
  fetch(`${apiBase}projects`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(projectValidData)
  })
  .then(response => {
    if (!response.ok) {
      // Get the error details from the response
      return response.text().then(text => {
        console.error('Backend validation error details:', text);
        throw new Error(`HTTP error! status: ${response.status} - ${text}`);
      });
    }
    return response.json();
  })
  .then(async (data) => {
    // Upload project image — use parent-owned ref (emitted directly from child on file select)
    const imageToUpload = selectedProjectImage.value;
    console.log('🔍 Image check at upload time — selectedProjectImage.value:', imageToUpload?.name || 'NULL');
    if (imageToUpload) {
      console.log('📸 Uploading project image:', imageToUpload.name);
      try {
        await uploadProjectImage(data.id, imageToUpload);
        console.log('✅ Project image uploaded');
      } catch (imgErr) {
        console.warn('⚠️ Project image upload failed, continuing anyway:', imgErr);
      }
    } else {
      console.log('ℹ️ No project image selected, skipping upload');
    }
    
    const projectId = data.id;
    
    // Upload budget document if available
    // if (projectScheduleInformationFormData.budgetDocument) { // REMOVED: No schedule section
    //   uploadBudgetDocument(data.id, projectScheduleInformationFormData.budgetDocument);
    // }
    
    // uploadedBaselineDocument(data.id, projectBaselineDocumentsFormData); // TODO: Comment this as Upload Documents is not used as of this moment - PLEASE DO NOT DELETE
    // uploadedDrawDocument(data.id, projectDrawDocumentsFormData); // TODO: Comment this as Upload Documents is not used as of this moment - PLEASE DO NOT DELETE
    
    // Prepare complete project data for confirmation screen
    const completeProjectData = {
      id: projectId,
      name: projectInformationFormData.buildingName,
      address_line_1: projectInformationFormData.streetAddress,
      address_line_2: null,
      city: projectInformationFormData.city,
      state: projectInformationFormData.state,
      zipcode: projectInformationFormData.zip,
      latitude: projectValidData.project.general_info.latitude,
      longitude: projectValidData.project.general_info.longitude,
      total_gsf: Number(projectInformationFormData.totalSquareFeetPerUse),
      num_units: Number(projectInformationFormData.totalNumberOfUnits),
      num_floors: projectInformationFormData.buildingHeightStories,
      parking_count: projectInformationFormData.subTotalParkingNumSpaces,
      
      // Extract construction types from the actual nested data structure
      project_construction_types_s: (projectValidData.project.construction_info?.construction_types || []).map(item => {
        // Look up the actual construction type name from the dropdown store
        const constructionType = dropdownStore.constructionTypes.find(ct => ct.id === item.construction_type_id);
        return constructionType ? constructionType.code : `Construction Type ID: ${item.construction_type_id}`;
      }),
      
      // Extract building use types from the actual nested data structure  
      former_use_types_s: (projectValidData.project.building_use_info?.new_uses || []).map(item => {
        // Look up the actual building use type name from the dropdown store using sub_category_id
        if (item.sub_category_id) {
          const buildingUseType = dropdownStore.buildingUseTypes.find(but => but.id === item.sub_category_id);
          return buildingUseType ? buildingUseType.code : `Use Type ID: ${item.sub_category_id}`;
        } else {
          // If no subcategory, look up the main asset category
          const assetCategory = dropdownStore.assetCategoryTypes.find(act => act.id === item.asset_category_id);
          return assetCategory ? assetCategory.code : `Asset Category ID: ${item.asset_category_id}`;
        }
      }),
      
      // Extract team information from nested structure
      team_contractors: (projectValidData.project.team_info?.contractor_info?.contractors || []).map(contractor => {
        return contractor.name || contractor.company_name || 'Unnamed Contractor';
      }),
      team_consultants: (projectValidData.project.team_info?.consultant_info?.consultants || []).map(consultant => {
        return consultant.name || consultant.company_name || 'Unnamed Consultant';
      }),
      team_investors: (projectValidData.project.team_info?.investor_sponsor_info?.investors || []).map(investor => {
        return investor.name || investor.company_name || 'Unnamed Investor';
      }),
      
      // Extract amenities
      amenities: (projectValidData.project.amenities_info?.amenities || []).map(amenity => {
        return amenity.name || `Amenity ID: ${amenity.amenity_type_id}`;
      }),
      
      primaryGeneralContractor: projectTeamInformationFormData.primaryGeneralContractor,
      image_url: projectInformationFormData.uploadedImage ? URL.createObjectURL(projectInformationFormData.uploadedImage) : null
    };
    
    // Navigate to project summary and auto-open upload modal there
    router.push(`/portfolio/project-summary/${projectId}?openUpload=true`);
  })
  .catch(error => {
    console.error('Project creation error:', error);
    errorStore.setError('Project Creation', `An error occurred. The project creation was unsuccessful: ${error.message}`, 'error', false);
  });
}

// Function to upload budget document with template-aware processing
function uploadBudgetDocument(projectId, budgetDocument) {
  const documentType = detectDocumentType(budgetDocument.name);
  
  // SKIP G702 PROCESSING - Only process valuable documents
  if (documentType === 'g702') {
    console.log('🚫 Skipping G702 document processing - focusing on valuable documents only:', budgetDocument.name);
    console.log('💡 G702s are organizational containers. Real data is in G703s, XLS, and Lien Waivers.');
    return; // Skip G702 upload entirely
  }
  
  // Only process valuable documents: G703, XLS, Budget, Lien Waivers
  const valuableTypes = ['g703', 'budget', 'excel', 'lien_waiver'];
  const isValueableDocument = valuableTypes.includes(documentType) || 
                             budgetDocument.name.toLowerCase().includes('.xls') ||
                             budgetDocument.name.toLowerCase().includes('lien') ||
                             budgetDocument.name.toLowerCase().includes('waiver');
  
  if (!isValueableDocument) {
    console.log('🔍 Unknown document type - uploading for analysis:', budgetDocument.name);
  }
  
  const formData = new FormData();
  formData.append('document[file]', budgetDocument);
  formData.append('document[document_type]', documentType);
  formData.append('document[project_id]', projectId);
  formData.append('document[auto_process]', 'true'); // Enable auto-processing for production
  
  // Enhanced processing for G703 documents
  if (documentType === 'g703') {
    // Use Template 33 (Standard G703 Mapping) for template-aware processing
    const defaultG703TemplateId = 33; // Template 33 with complete 18-column mapping
    formData.append('document[template_id]', defaultG703TemplateId);
    console.log('✅ G703 document detected - using Template 33 for enhanced OCR processing');
  }
  
  // Enhanced processing for Excel/Budget documents  
  if (documentType === 'budget' || budgetDocument.name.toLowerCase().includes('.xls')) {
    console.log('✅ Excel/Budget document detected - will use enhanced XLS processor');
  }
  
  // Use the main documents endpoint for proper processing (no auth required)
  fetch(`${apiBase}documents`, {
    method: 'POST',
    body: formData
  })
  .then(response => {
    if (!response.ok) {
      throw new Error('Failed to upload document');
    }
    return response.json();
  })
  .then(data => {
    console.log('Document uploaded successfully with optimized processing:', data);
    if (documentType === 'g703') {
      console.log('G703 document will be processed with Template 33 column mappings for enhanced accuracy');
    } else if (documentType === 'budget' || budgetDocument.name.toLowerCase().includes('.xls')) {
      console.log('Excel document will be processed with enhanced filtering and validation');
    }
  })
  .catch(error => {
    errorStore.setError('Document Upload', `Failed to upload document: ${error.message}`, 'error', false);
  });
}

// Helper function to detect document type from filename
function detectDocumentType(filename) {
  const lowerName = filename.toLowerCase();
  
  // G703 detection - continuation sheets (highest value)
  if (lowerName.includes('g703') || lowerName.includes('continuation')) {
    return 'g703';
  }
  
  // G702 detection - application/certificate for payment (skip these)
  if (lowerName.includes('g702') || 
      lowerName.includes('application') || 
      lowerName.includes('certificate') ||
      lowerName.match(/aia.*702/i) ||
      lowerName.includes('payment')) {
    return 'g702';
  }
  
  // Excel/Budget files (high value for structured data)
  if (lowerName.includes('budget') || 
      lowerName.includes('excel') || 
      lowerName.includes('.xlsx') || 
      lowerName.includes('.xls')) {
    return 'budget';
  }
  
  // Lien waivers (legal completion proof)
  if (lowerName.includes('lien') || lowerName.includes('waiver')) {
    return 'lien_waiver';
  }
  
  return 'unknown';
}

// Function to build a structured list of new uses with their subcategories from a form
function buildNewUsesWithSubCategories(form) {
  // Retrieve updated use categories from a reference object
  const newUseCategoriesWithSubCategories = projectInformationRef.value.updatedUseCategories;
  // Initialize an empty array to hold the result
  const result = [];

  // Helper function to add a category or subcategory to the result array
  const addCategoryResult = (category, subCategory = null) => {
    // Base information structure for each category/subcategory
    const baseInfo = {
      asset_category_id: parseInt(category.id), // Convert category ID to integer
      sub_category_id: subCategory?.id ? parseInt(subCategory.id) : null, // Convert subcategory ID to integer, if present
      total_gsf: 0, // Default gross square footage
      num_units: 0, // Default number of units
      brand_type_id: null, // Default brand type ID
      affordable_comp_gsf: null, // Default affordable component GSF
      affordable_comp_num_units: null // Default affordable component number of units
    };

    // If a subcategory is provided, update the base information with specific values
    if (subCategory) {
      // Check if a brand type ID exists for the subcategory
      if (form.subCategoriesBrandType[subCategory.id]?.id) {
        // Update base information with subcategory-specific values, excluding brand name
        Object.assign(baseInfo, {
          total_gsf: form.subCategoriesSquareFeetPerUse[subCategory.id] || 0,
          num_units: form.subCategoriesNumberOfUnits[subCategory.id] || 0,
          brand_type_id: form.subCategoriesBrandType[subCategory.id]?.id || null,
          affordable_comp_gsf: form.subCategoriesAffordableCompGSF[subCategory.id] || null,
          affordable_comp_num_units: form.subCategoriesAffordableCompNumUnits[subCategory.id] || null
        });
      } else {
        // Update base information with subcategory-specific values, including brand name
        Object.assign(baseInfo, {
          total_gsf: form.subCategoriesSquareFeetPerUse[subCategory.id] || 0,
          num_units: form.subCategoriesNumberOfUnits[subCategory.id] || 0,
          brand_type_id: form.subCategoriesBrandType[subCategory.id]?.id || null,
          brand_name: form.subCategoriesBrandType[subCategory.id]?.code || null,
          affordable_comp_gsf: form.subCategoriesAffordableCompGSF[subCategory.id] || null,
          affordable_comp_num_units: form.subCategoriesAffordableCompNumUnits[subCategory.id] || null
        });
      }
    } else {
      // If no subcategory is provided, update the base information with category-specific values
      Object.assign(baseInfo, {
        total_gsf: form.newUseCategoriesSquareFeetPerUse[category.id] || 0,
        num_units: form.newUseCategoriesNumberOfUnits[category.id] || 0
      });
    }

    // Add the updated base information to the result array
    result.push(baseInfo);
  };

  // Iterate over each category ID and its enabled status from the form
  Object.entries(form.newUseCategoryIds).forEach(([categoryId, isEnabled]) => {
    // Skip if the category is not enabled
    if (!isEnabled) return;

    // Find the category object by its ID
    const category = newUseCategoriesWithSubCategories.find(cat => cat.id.toString() === categoryId);
    // Skip if the category was not found
    if (!category) return;

    // Check if the category has subcategories
    if (category.subCategories && category.subCategories.length > 0) {
      // Iterate over each subcategory ID and its enabled status
      Object.entries(form.subCategoryIds).forEach(([subCategoryId, isSubEnabled]) => {
        // Skip if the subcategory is not enabled
        if (!isSubEnabled) return;

        // Find the subcategory object by its ID within the current category
        const subCategory = category.subCategories.find(sub => sub.id.toString() === subCategoryId);
        // Skip if the subcategory was not found
        if (!subCategory) return;

        // Add the category and subcategory to the result
        addCategoryResult(category, subCategory);
      });
    } else {
      // If there are no subcategories, add just the category to the result
      addCategoryResult(category);
    }
  });

  // Return the structured list of new uses with their subcategories
  return result;
}

// Function to build an array of parking types objects based on form input
function buildNewParkingTypes(form) {
  // Initialize an empty array to hold the result
  const result = [];

  // Iterate over each parking type ID and its enabled status from the form
  Object.entries(form.newParkingTypeIds).forEach(([parking_type_id, isEnabled]) => {
    // Check if the parking type is enabled
    if (isEnabled) {
      // Retrieve the total gross square footage for the parking type, defaulting to 0 if not specified
      const total_gsf = form.parkingTypesSquareFeetPerUse[parking_type_id] || 0;
      // Retrieve the number of spaces for the parking type, defaulting to 0 if not specified
      const num_spaces = form.parkingTypesNumberOfSpaces[parking_type_id] || 0;

      // Add a new object to the result array with the parking type ID, total GSF, and number of spaces
      result.push({
        parking_type_id: parseInt(parking_type_id), // Convert the parking type ID to an integer
        total_gsf, // Total gross square footage for this parking type
        num_spaces, // Number of parking spaces for this parking type
      });
    }
  });

  // Return the array of parking types objects
  return result;
}

function buildNewConstructionTypes(constructionTypes, constructionTypeAdaptiveReuse) {
  // Handle both array and object formats for constructionTypes
  if (!constructionTypes) {
    return [];
  }
  
  // If it's already an array, return it as-is or process it
  if (Array.isArray(constructionTypes)) {
    if (constructionTypes.length === 0) {
      return [];
    }
    // If array contains objects with the expected structure, process them
    return constructionTypes.map(constructionType => {
      let newConstructionType = {
        construction_type_id: constructionType.id,
        former_asset_category_id: null
      };

      if (constructionType.code == 'Adaptive Reuse') {
        newConstructionType.former_asset_category_id = constructionTypeAdaptiveReuse;
      }

      return newConstructionType;
    });
  }
  
  // If it's an object, convert to array first (legacy behavior)
  if (typeof constructionTypes === 'object' && Object.keys(constructionTypes).length === 0) {
    return [];
  }

  const newConstructionTypes = Object.values(constructionTypes).map(constructionType => {
    // Initialize the object with construction_type and former_asset_category_id as null
    let newConstructionType = {
      construction_type_id: constructionType.id,
      former_asset_category_id: null
    };

    // If the constructionType is Adaptive Reuse
    if (constructionType.code == 'Adaptive Reuse') {
      newConstructionType.former_asset_category_id = constructionTypeAdaptiveReuse;
    }

    return newConstructionType;
  });

  return newConstructionTypes;
}

function buildInvestors(form) {
  let investors = []; // Initialize an empty array to hold investor objects

  // Define an array of keys for primary investor fields in the form
  const primaryInvestors = ['primaryLender', 'primaryInvestor', 'primaryDeveloper'];

  // Iterate over primary investor fields and add non-empty objects to the investors array
  primaryInvestors.forEach(investorField => {
    const investor = form[investorField]; // Get the investor object from the form
    if (investor && Object.keys(investor).length > 0) { // Check if the object is not empty
      investors.push(investor); // Add the investor object to the investors array
    }
  });

  // Define an array of keys for fields that may contain multiple investors
  const fields = [
    'additionalLenders', 'additionalInvestors',
    'coDevelopers'
  ];

  // Iterate over fields that may contain multiple investors
  fields.forEach(field => {
    if (form[field] && Array.isArray(form[field])) { // Check if the field exists and is an array
      form[field].forEach(investor => { // Iterate over each investor in the field
        if (Object.keys(investor).length > 0) { // Check if the investor object is not empty
          investors.push(investor); // Add the investor object to the investors array
        }
      });
    }
  });

  return investors; // Return the array of investors
}

// Function to build an array of consultant objects from a form
function buildConsultants(form) {
  // Define an array of consultant field names expected in the form
  const fields = [
    'ownersRepresentative', 'additionalProjectOrConstructionManager',
    'architectOfRecord', 'designArchitect', 'civil', 'structural',
    'mechanical', 'electrical', 'plumbing', 'fireProtection',
    'interiorDesign', 'landscapeArchitect', 'geotechnical', 'environmental'
  ];
  
  // Initialize an empty array to hold consultant objects
  let consultants = [];
  
  // Iterate over each field name defined in the fields array
  fields.forEach(field => {
    // Check if the current field exists in the form
    if (form[field]) {
      // Assuming form[field] is an object structured as { name: ..., consultant_type_id: ... }
      // Directly push the object from the form into the consultants array
      consultants.push(form[field]);
    }
  });
  
  // Return the array of consultant objects
  return consultants;
}

// TODO: Comment this as Upload Documents is not used as of this moment - PLEASE DO NOT DELETE
// // For Project Upload 
// async function uploadedBaselineDocument (projectId, data) {
//   // Iterate in each container
//   // Developer Budget Files first
//   uploadDocuments(projectId, data.localForm.developerBudget.values, 'DeveloperBudget', data.localForm.date, null);
//   // General Contractor Budget Files
//   uploadDocuments(projectId, data.localForm.generalContractorBudget.values, 'GeneralContractorBudget', data.localForm.date, null);
//   // General Contractor Schedule Files
//   uploadDocuments(projectId, data.localForm.generalContractorSchedule.values, 'GeneralContractorSchedule', data.localForm.date, null);
//   // Executed Construction Contracts
//   uploadDocuments(projectId, data.localForm.executedConstructionContracts.values, 'ConstructionContracts', data.localForm.date, null);
// }

// async function uploadFile(file, document) {
//   try {
//     let keys = Object.keys(document);
//     let formData = new FormData();
//     formData.append('document[file]', file);
//     keys.forEach((key) => formData.append(`document[${key}]`, document[key]));

//     await axiosPostQuery(
//       `${apiBase}/documents`,
//       formData,
//       {
//         authToken,
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       },
//       () => {
//         console.log(`Document created`)
//       },
//       () => {
//         errorStore.setError('Document Upload', 'An error occurred. The document upload was unsuccessful.', 'error', false)
//       }
//     );

//   } catch (error) {
//     console.error('Error:', error);
//   }
// }

// async function uploadedDrawDocument (projectId, data) {
//   // Iterate in each container
//   // Borrower Requisition Letter
//   uploadDocuments(projectId, data.localForm.borrowerRequisitionLetter.values, 'BorrowerRequisitionLetters', data.localForm.periodThroughDate, data.localForm.drawNumber);
//   // Developer Draw Tracker
//   uploadDocuments(projectId, data.localForm.developerDrawTracker.values, 'SponsorDrawTracker', data.localForm.periodThroughDate, data.localForm.drawNumber);
//   // Full Payment Application
//   uploadDocuments(projectId, data.localForm.fullPaymentApplication.values, 'FullPaymentApplication', data.localForm.periodThroughDate, data.localForm.drawNumber);
//   // Soft Cost Invoices
//   uploadDocuments(projectId, data.localForm.softCostInvoices.values, 'SoftCostInvoices', data.localForm.periodThroughDate, data.localForm.drawNumber);
// }

// async function uploadDocuments (projectId, documentContainerArray, documentContainerName, date, drawNumber) {
//   const validDocuments = documentContainerArray?.length ? [...documentContainerArray] : []
//   const convertedDate = formattedDate(date);
//   const convertedDrawNumber = drawNumber ? '_' + drawNumber : '';
//   for (const file of validDocuments) {
//     const validFile = getFilenameAndExtension(file.name);
//     let document = {
//       filepath: validFile[0] + '_' + documentContainerName + '_' + convertedDate + `${convertedDrawNumber}` + validFile[1],
//       status: 'in_progress',
//       project_id: projectId,
//       period_through: convertedDate,
//       draw_number: drawNumber
//     };

//     await uploadFile(file, document);
//   }
// }

// const getFilenameAndExtension = (filename) => {
//   const fileNameWithExtension = filename;

//   const lastDotIndex = fileNameWithExtension.lastIndexOf('.');
//   if (lastDotIndex !== -1) {
//     const fileName = fileNameWithExtension.substring(0, lastDotIndex);
//     const fileExtension = fileNameWithExtension.substring(lastDotIndex);
//     return [fileName, fileExtension]
//   } else {
//     return null
//   }
// }

// function formattedDate(date) {
//   const dateObj = new Date(date);
//   const year = dateObj.getFullYear();
//   const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are zero-based
//   const day = String(dateObj.getDate()).padStart(2, '0');
//   return `${year}-${month}-${day}`;
// }

// Function to upload project image
async function uploadProjectImage(projectId, imageFile) {
  console.log('📸 Uploading project image:', imageFile.name, 'to project ID:', projectId);
  
  const formData = new FormData();
  formData.append('document[file]', imageFile);
  formData.append('document[project_id]', projectId);
  formData.append('document[document_type]', 'project_image');
  formData.append('document[description]', imageFile.name);
  formData.append('document[is_primary]', true);
  
  try {
    const response = await axios.post(`${apiBase}documents/`, formData, {
      headers: {
        ...authStore.getAuthTokenHeader?.headers,
        'Content-Type': 'multipart/form-data'
      }
    });
    console.log('✅ Project image uploaded successfully, ID:', response.data?.id);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to upload project image:', error.response?.status, error.response?.data || error.message);
    // Don't re-throw — image upload failure should not block project navigation
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.new-project-view-container {
  margin-top: 60px;
  padding: 15px;
  font-family: 'Inter';

  .new-project-header-container {
    padding-bottom: 30px;
    margin-bottom: 30px;
    border-bottom: 2px solid lightGray;
  }

  .new-project-main-title {
    font-size: 24px;
    font-weight: bolder;
    color: $navyBlue;
    margin-bottom: 15px;
  }

  .breadcrumb-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 15px;
    font-size: 14px;
    color: #8C8D92;
  }
}

/* Container Accordion -- Start */
.accordion-panel-header {
  display: flex;
  align-items: center;
  position: relative;
  width: 99%;
  margin: 15px 0;
  color: $navyBlue;
  font-weight: bolder;

  label {
    font-size: 20px;
  }
}

/* Container Accordion -- End */

.divider {
  border-bottom: 2px solid lightgray;
}

.project-action-button {
  display: flex;
  justify-content: flex-end;
  padding: 15px 0;

  button {
    background-color: $navyBlue;
    color: white;
    border: none;
    border-radius: 5px;
    padding: 10px 20px;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
    
    &:hover {
      background-color: darken($navyBlue, 10%);
    }
  }
}
</style>